import React, { useState } from "react";
import {
  useAppSelector,
  useAppDispatch,
  setPrimaryComputer,
  setBackupComputer,
} from "../../../../store";
import {
  Computer,
  ComputerModel,
  ComputerOption,
} from "../../../../types/computer";
import { COMPUTERS, COMPUTER_OPTIONS } from "../../../../constants/computer";
import { calculateComputerStats } from "../../../../utils/calculations/computerCalculations";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import { BreakawayComputerConfig } from "./BreakawayComputerConfig";
import { selectIsBreakaway } from "../../../../store/selectors/breakawayHullSelectors";

export const ComputerSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const computer = useAppSelector((state) => state.shipBuilder.computer);
  const techLevel = useAppSelector((state) => state.shipBuilder.techLevel);
  const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive);
  const isBreakaway = useAppSelector(selectIsBreakaway);
  const breakawayHull = useAppSelector((state) => state.breakawayHull);

  const [selectedPrimaryModel, setSelectedPrimaryModel] = useState<
    ComputerModel | ""
  >(computer?.primary?.model || "");
  const [selectedBackupModel, setSelectedBackupModel] = useState<
    ComputerModel | ""
  >(computer?.backup?.model || "");
  const [primaryOptions, setPrimaryOptions] = useState<ComputerOption[]>(
    computer?.primary?.options || [],
  );
  const [backupOptions, setBackupOptions] = useState<ComputerOption[]>(
    computer?.backup?.options || [],
  );

  // Check if breakaway hulls are configured
  const hasBreakawayHulls = isBreakaway || breakawayHull?.isBreakaway;

  const getBreakawayInfo = () => {
    if (!breakawayHull?.sections) return "";
    const sections = breakawayHull.sections;
    return `${sections.length} section${sections.length > 1 ? 's' : ''} (${sections.map(s => `${s.name}: ${s.size} tons`).join(", ")})`;
  };

  // Filter computers by tech level
  const availableComputers = Object.values(COMPUTERS).filter(
    (comp) => tlOverrideActive || comp.techLevel <= techLevel
  );

  const handlePrimaryModelChange = (model: ComputerModel) => {
    setSelectedPrimaryModel(model);

    if (model) {
      const computerDef = COMPUTERS[model];
      const newPrimary: Computer = {
        model,
        type: computerDef.type,
        processing: computerDef.processing,
        techLevel: computerDef.techLevel,
        cost: computerDef.baseCost,
        options: primaryOptions,
        isPrimary: true,
        isBackup: false,
      };
      dispatch(setPrimaryComputer(newPrimary));
    } else {
      dispatch(setPrimaryComputer(null));
    }
  };

  const handleBackupModelChange = (model: ComputerModel) => {
    setSelectedBackupModel(model);

    if (model) {
      const computerDef = COMPUTERS[model];
      const newBackup: Computer = {
        model,
        type: computerDef.type,
        processing: computerDef.processing,
        techLevel: computerDef.techLevel,
        cost: computerDef.baseCost,
        options: backupOptions,
        isPrimary: false,
        isBackup: true,
      };
      dispatch(setBackupComputer(newBackup));
    } else {
      dispatch(setBackupComputer(null));
    }
  };

  const handlePrimaryOptionChange = (
    option: ComputerOption,
    checked: boolean,
  ) => {
    let newOptions: ComputerOption[];

    if (checked) {
      newOptions = [...primaryOptions.filter((opt) => opt !== "both"), option];
    } else {
      newOptions = primaryOptions.filter((opt) => opt !== option);
    }

    setPrimaryOptions(newOptions);

    if (selectedPrimaryModel) {
      const computerDef = COMPUTERS[selectedPrimaryModel];
      const updatedPrimary: Computer = {
        model: selectedPrimaryModel,
        type: computerDef.type,
        processing: computerDef.processing,
        techLevel: computerDef.techLevel,
        cost: computerDef.baseCost,
        options: newOptions,
        isPrimary: true,
        isBackup: false,
      };
      dispatch(setPrimaryComputer(updatedPrimary));
    }
  };

  const handleBackupOptionChange = (
    option: ComputerOption,
    checked: boolean,
  ) => {
    let newOptions: ComputerOption[];

    if (checked) {
      newOptions = [...backupOptions.filter((opt) => opt !== "both"), option];
    } else {
      newOptions = backupOptions.filter((opt) => opt !== option);
    }

    setBackupOptions(newOptions);

    if (selectedBackupModel) {
      const computerDef = COMPUTERS[selectedBackupModel];
      const updatedBackup: Computer = {
        model: selectedBackupModel,
        type: computerDef.type,
        processing: computerDef.processing,
        techLevel: computerDef.techLevel,
        cost: computerDef.baseCost,
        options: newOptions,
        isPrimary: false,
        isBackup: true,
      };
      dispatch(setBackupComputer(updatedBackup));
    }
  };

  const stats = calculateComputerStats({ computer });

  return (
    <div className="space-y-6 pb-24">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Computer Systems Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Select primary and backup computer systems for your ship
        </p>
      </div>

      {/* Breakaway Hulls Detection */}
      {hasBreakawayHulls && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
              Breakaway Hulls Detected
            </h3>
            <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
              {getBreakawayInfo()}
            </p>
          </div>
        </div>
      )}

      {/* Breakaway Computer Configuration - Inline */}
      {hasBreakawayHulls && breakawayHull?.sections && breakawayHull.sections.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Breakaway Section Computer Configuration</h3>
          <BreakawayComputerConfig
            isOpen={true}
            onClose={() => {}} // No-op since we're showing inline
            inline={true}
          />
        </div>
      )}

      {/* Standard Computer Selection - Hidden when breakaway hulls are enabled */}
      {!hasBreakawayHulls && (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Standard Computer Configuration
        </h3>
        
        {/* Primary Computer Selection */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Primary Computer
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Computer Model
              </label>
              <select
                value={selectedPrimaryModel}
                onChange={(e) =>
                  handlePrimaryModelChange(e.target.value as ComputerModel)
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">Select a computer model</option>
                {availableComputers.map((comp) => (
                  <option key={comp.model} value={comp.model}>
                    {comp.model} (Processing: {comp.processing}, TL:{" "}
                    {comp.techLevel}, Cost: {comp.baseCost.toLocaleString()}{" "}
                    Cr)
                  </option>
                ))}
              </select>
            </div>

            {selectedPrimaryModel && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Options
                </label>
                <div className="space-y-2">
                  {["jumpControl", "hardened"].map((key) => (
                    <label key={key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={primaryOptions.includes(key as ComputerOption)}
                        onChange={(e) =>
                          handlePrimaryOptionChange(
                            key as ComputerOption,
                            e.target.checked,
                          )
                        }
                        className="mr-2 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {
                          COMPUTER_OPTIONS[key as keyof typeof COMPUTER_OPTIONS]
                            .name
                        }
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Backup Computer Selection */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Backup Computer (Optional)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Computer Model
              </label>
              <select
                value={selectedBackupModel}
                onChange={(e) =>
                  handleBackupModelChange(e.target.value as ComputerModel)
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">No backup computer</option>
                {availableComputers
                  .filter(
                    (comp) =>
                      !selectedPrimaryModel ||
                      comp.processing <
                        COMPUTERS[selectedPrimaryModel].processing,
                  )
                  .map((comp) => (
                    <option key={comp.model} value={comp.model}>
                      {comp.model} (Processing: {comp.processing}, TL:{" "}
                      {comp.techLevel}, Cost: {comp.baseCost.toLocaleString()}{" "}
                      Cr)
                    </option>
                  ))}
              </select>
            </div>

            {selectedBackupModel && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Options
                </label>
                <div className="space-y-2">
                  {["jumpControl", "hardened"].map((key) => (
                    <label key={key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={backupOptions.includes(key as ComputerOption)}
                        onChange={(e) =>
                          handleBackupOptionChange(
                            key as ComputerOption,
                            e.target.checked,
                          )
                        }
                        className="mr-2 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {
                          COMPUTER_OPTIONS[key as keyof typeof COMPUTER_OPTIONS]
                            .name
                        }
                      </span>
                    </label>
                  ))}
                </div>
                
                {/* Note about backup computer /bis option */}
                {backupOptions.includes("jumpControl") && (
                  <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                          Note: Jump Control Specialisation on Backup Computer
                        </h4>
                        <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                          The Jump Control Specialisation (/bis) option on the backup computer will not reduce software bandwidth requirements. Only the primary computer's /bis option affects running software, since only the primary computer is active during normal operations.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>

        {/* Validation Errors */}
        {!stats.success && stats.errors && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md">
            <h4 className="text-red-800 dark:text-red-200 font-semibold mb-2">
              Validation Errors:
            </h4>
            <ul className="text-red-700 dark:text-red-300 text-sm space-y-1">
              {stats.errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      )}

      {/* Sticky Footer */}
      {!hasBreakawayHulls && stats.success && stats.data && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 z-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-xs text-gray-300 mb-1">TOTAL COST</div>
                <div className="text-lg font-bold text-green-400">
                  {stats.data.totalCost.toLocaleString()} Cr
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-300 mb-1">
                  PRIMARY PROCESSING
                </div>
                <div className="text-lg font-bold text-blue-400">
                  {stats.data.primaryProcessing}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-300 mb-1">
                  BACKUP PROCESSING
                </div>
                <div className="text-lg font-bold text-purple-400">
                  {stats.data.backupProcessing}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-300 mb-1">
                  EFFECTIVE PROCESSING
                </div>
                <div className="text-lg font-bold text-yellow-400">
                  {stats.data.totalProcessing}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Breakaway Hulls Message - Show when breakaway hulls are enabled but no sections configured */}
      {hasBreakawayHulls && !breakawayHull?.sections?.length && (
        <div className="mb-6 p-4 border-2 border-yellow-300 dark:border-yellow-600 rounded-lg bg-yellow-50 dark:bg-yellow-900/30">
          <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
            Breakaway Hulls Detected
          </h3>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            Breakaway hulls are enabled but sections are not yet configured. 
            Please configure breakaway sections in the Hull step before configuring computers.
          </p>
        </div>
      )}
    </div>
  );
};
